import * as Sentry from "@sentry/browser";
import { FindexClient } from "@/integrations/findex";
import {
    EInviteStatus,
  ENotificationLevel,
  ENotificationStatus,
  getRefId,
  type INotification,
  type ISavedDocument,
} from "findef";
import { uniqueBy } from "math";
import { useFindexSocket } from "../useFindexSocket";
import { pushToast } from "components/toast/hooks";
import { playAudio } from "@/utils/audio";
import MessageAudio from "assets/audio/message.mp3?url";
import { registerSharedCacheReset } from "@/utils/sharedCacheReset";

// After the initial /user/notifications fetch rejects we re-arm the singleton
// guard so a future consumer can retry. Without a cooldown, every component
// that calls useNotifications() during the same login burst (navbar,
// navbar-mobile, notifications-window, …) would immediately re-fire the
// fetch — defeating the whole point of the singleton and turning a single
// transient 500 into a thundering herd against the rate limiter.
const NOTIFICATION_FETCH_FAILURE_COOLDOWN_MS = 30_000;
let lastFetchFailureAt = -Infinity;

export type UseNotifications = {
  notifications: ComputedRef<Array<ISavedDocument<INotification, string>>>;
  isLoading: ComputedRef<boolean>;
  markAllRead: () => Promise<void>;
  setNotificationStatus: (notificationId: string, status: ENotificationStatus) => Promise<void>;
};

const updateIsLoading = ref<boolean>(false);
const fetchLoading = ref<boolean>(false);
const notifications = ref<Array<ISavedDocument<INotification, string>>>([]);
// Module-level so the initial /user/notifications fetch runs once for the
// page lifetime, regardless of how many components (navbar, navbar-mobile,
// notifications-window, …) call useNotifications(). Without this guard each
// caller mounts its own useAsyncFetch and the same query fires 3-5× per
// login burst.
let fetchedOnce = false;
let socketListenerInitialized = false;

const addNotifications = (
  incoming: Array<ISavedDocument<INotification, string>>,
) => {
  const nextNotifications: Array<ISavedDocument<INotification, string>> = [
    ...notifications.value,
    ...(incoming || []),
  ];
  const uniqueNotifications = uniqueBy(nextNotifications, (it) =>
    getRefId(it),
  );
  notifications.value = uniqueNotifications;
};

const fetchUnreadNotifications = async () => {
  fetchLoading.value = true;
  try {
    const [items] = await FindexClient.getNotifications(
      ENotificationStatus.UNREAD,
    );
    return items || [];
  } finally {
    fetchLoading.value = false;
  }
};

const ensureInitialFetch = () => {
  if (fetchedOnce) return;
  // Suppress retries inside the cooldown window so a reliably-failing
  // endpoint cannot drive a stampede of refetches from every consumer's
  // setup() pass.
  if (performance.now() - lastFetchFailureAt < NOTIFICATION_FETCH_FAILURE_COOLDOWN_MS) return;
  fetchedOnce = true;
  fetchUnreadNotifications()
    .then((items) => addNotifications(items))
    .catch((e) => {
      lastFetchFailureAt = performance.now();
      fetchedOnce = false;
      Sentry.captureException(e, {
        tags: { component: "useNotifications", action: "initial_fetch" },
      });
    });
};

// Reset on logout so a re-login as a different account never shows the
// previous user's notifications. Registered eagerly at module-eval time
// so the hook is wired even if no component has called useNotifications()
// yet (e.g. an Auth0 session expiry that triggers session.kill before
// any notification UI has mounted).
registerSharedCacheReset(() => {
  fetchedOnce = false;
  lastFetchFailureAt = -Infinity;
  notifications.value = [];
});

// Initialize socket listener only once globally
const initializeSocketListener = () => {
  if (socketListenerInitialized) return;
  socketListenerInitialized = true;

  const socket = useFindexSocket();

  socket.onReady(() => {
    socket.onMessage((msg) => {
      const app = msg.getString("app");
      if (app !== "notifications") return;
      const notif = msg.getKeyValueStore("notification");
      if (!notif) return;
      const notification: ISavedDocument<INotification, string> =
        notif.toJS() as unknown as any as ISavedDocument<INotification, string>;

      addNotifications([notification]);

      pushToast({
        title: notification.title || "Notification",
        subtitle:
          notification.body ||
          notification.title ||
          "You have a new notification",
        variant:
          notification.level === ENotificationLevel.WARNING
            ? "warning"
            : notification.level === ENotificationLevel.ERROR
              ? "error"
              : "success",
        duration: 10,
      });

      playAudio(MessageAudio);
    });
  });
};

const refetchAndReplace = async () => {
  const items = await fetchUnreadNotifications();
  notifications.value = [];
  addNotifications(items);
};

export const useNotifications = (): UseNotifications => {
  ensureInitialFetch();
  initializeSocketListener();

  const markAllReadLoading = ref<boolean>(false);

  const markAllRead = async () => {
    if (markAllReadLoading.value) return;
    markAllReadLoading.value = true;
    const previous = notifications.value;
    try {
      const filteredNotifications = previous.filter((it) => {
        if (it.invite && typeof it.invite !== "string") {
          return (it.invite as unknown as any)?.status !== EInviteStatus.PENDING;
        }
        return it;
      });
      // Optimistic: clear locally so the UI updates immediately and we
      // avoid a follow-up GET /notifications (rate-limit pressure on
      // mobile, where this can fire next to a navigation).
      notifications.value = [];
      if (filteredNotifications.length > 0) {
        try {
          await FindexClient.updateNotificationStatuses({
            notificationIds: filteredNotifications.map((it) => it._id as string),
            status: ENotificationStatus.READ,
          });
        } catch (e) {
          // Roll back on failure and refetch to get truth from server.
          notifications.value = previous;
          await refetchAndReplace();
          throw e;
        }
      }
    } finally {
      markAllReadLoading.value = false;
    }
  };

  const isLoading = computed(
    (): boolean => fetchLoading.value || markAllReadLoading.value || updateIsLoading.value,
  );

  const setNotificationStatus = async (notificationId: string, status: ENotificationStatus) => {
    updateIsLoading.value = true;
    const previous = notifications.value;
    // Optimistic: remove the one item locally so the UI updates without
    // a follow-up GET /notifications. Same rationale as markAllRead.
    notifications.value = previous.filter((n) => getRefId(n) !== notificationId);
    try {
      await FindexClient.setNotificationStatus(notificationId, status);
    } catch (e) {
      notifications.value = previous;
      await refetchAndReplace();
      throw e;
    } finally {
      updateIsLoading.value = false;
    }
  }

  return {
    notifications: computed(
      (): Array<ISavedDocument<INotification, string>> => notifications.value,
    ),
    isLoading: isLoading,
    setNotificationStatus,
    markAllRead,
  };
};
